/**
 * Auth Storage Migration Utility
 *
 * Migrates localStorage from old key 'auth-storage' to new key 'teletienda-auth'
 * for SSO compatibility across *.teletienda.ar subdomains.
 *
 * This migration runs once on app load (before AuthProvider initializes)
 * and handles existing user sessions gracefully.
 */

const OLD_KEY = 'auth-storage';
const NEW_KEY = 'teletienda-auth';

/**
 * Storage format used by shared-auth-ui (zustand/persist)
 */
interface NewStorageFormat {
  state: {
    user: unknown;
    token: string | null;
    refreshToken: string | null;
    expiresAt: number | null;
    isAuthenticated: boolean;
  };
  version?: number;
}

/**
 * Old storage format (may vary based on previous implementation)
 */
interface OldStorageFormat {
  // zustand/persist format
  state?: {
    user?: unknown;
    token?: string;
    refreshToken?: string;
    expiresAt?: number;
    isAuthenticated?: boolean;
  };
  // Legacy direct format
  user?: unknown;
  token?: string;
  refreshToken?: string;
  expiresAt?: number;
  isAuthenticated?: boolean;
  version?: number;
}

/**
 * Migrate auth storage from old key to new key
 *
 * Call this function once on app load, BEFORE AuthProvider initializes.
 * It will:
 * 1. Check if old storage key exists
 * 2. If old exists and new doesn't - migrate data
 * 3. Transform format if needed
 * 4. Clear old key after migration
 */
export function migrateAuthStorage(): void {
  // Skip in non-browser environments
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

  const oldData = localStorage.getItem(OLD_KEY);
  const newData = localStorage.getItem(NEW_KEY);

  // Case 1: No old data - nothing to migrate
  if (!oldData) {
    return;
  }

  // Case 2: Both exist - just remove old key
  if (oldData && newData) {
    localStorage.removeItem(OLD_KEY);
    console.log('[Auth Migration] Removed old storage key (new key already exists)');
    return;
  }

  // Case 3: Old exists, new doesn't - migrate
  try {
    const parsed: OldStorageFormat = JSON.parse(oldData);

    // Transform to new format
    const newFormat: NewStorageFormat = transformToNewFormat(parsed);

    // Validate we have meaningful data to migrate
    if (newFormat.state.token || newFormat.state.user) {
      localStorage.setItem(NEW_KEY, JSON.stringify(newFormat));
      console.log('[Auth Migration] Successfully migrated auth storage from', OLD_KEY, 'to', NEW_KEY);
    }

    // Remove old key regardless
    localStorage.removeItem(OLD_KEY);
  } catch (error) {
    console.error('[Auth Migration] Failed to migrate:', error);
    // Clear corrupted old data
    localStorage.removeItem(OLD_KEY);
  }
}

/**
 * Transform old storage format to new format
 */
function transformToNewFormat(oldData: OldStorageFormat): NewStorageFormat {
  // Check if already in zustand/persist format
  if (oldData.state) {
    return {
      state: {
        user: oldData.state.user ?? null,
        token: oldData.state.token ?? null,
        refreshToken: oldData.state.refreshToken ?? null,
        expiresAt: oldData.state.expiresAt ?? null,
        isAuthenticated: oldData.state.isAuthenticated ?? false,
      },
      version: oldData.version ?? 0,
    };
  }

  // Legacy direct format (not wrapped in state)
  return {
    state: {
      user: oldData.user ?? null,
      token: oldData.token ?? null,
      refreshToken: oldData.refreshToken ?? null,
      expiresAt: oldData.expiresAt ?? null,
      isAuthenticated: oldData.isAuthenticated ?? false,
    },
    version: 0,
  };
}
